import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ARCHIVE, RESIDENTS, UPCOMING } from '@/data/events';
import { useI18n } from '@/providers/i18n';

import ArtistHero from './components/ArtistHero';
import ArtistLinks from './components/ArtistLinks';
import ArtNav from './components/ArtNav';
import BookingBlock from './components/BookingBlock';
import BookingForm from './components/BookingForm';
import PlayedList, { type PlayedEvent } from './components/PlayedList';
import SignatureList from './components/SignatureList';

export default function ArtistDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();
  const { t } = useI18n();
  const [bookOpen, setBookOpen] = useState(false);

  const idx = RESIDENTS.findIndex((a) => a.slug === slug);
  const a = idx >= 0 ? RESIDENTS[idx] : null;
  const prev =
    idx > 0 ? RESIDENTS[idx - 1] : RESIDENTS[RESIDENTS.length - 1];
  const next =
    idx >= 0 && idx < RESIDENTS.length - 1 ? RESIDENTS[idx + 1] : RESIDENTS[0];

  useEffect(() => {
    if (!a) return;
    const onKey = (e: KeyboardEvent) => {
      if (bookOpen) return;
      if (e.key === 'ArrowLeft' && prev) nav(`/artists/${prev.slug}`);
      if (e.key === 'ArrowRight' && next) nav(`/artists/${next.slug}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [a, prev, next, bookOpen, nav]);

  if (!a || !prev || !next) {
    return (
      <section style={{ padding: '120px 0' }}>
        <h1 className="upper tight" style={{ fontSize: 48 }}>
          404 / NO ARTIST
        </h1>
        <p className="fg-dim">No resident with slug &ldquo;{slug}&rdquo;.</p>
        <Link to="/artists" className="btn btn-arrow" style={{ marginTop: 24 }}>
          {t('cta.back')}
        </Link>
      </section>
    );
  }

  const played: PlayedEvent[] = a.played
    .map((code) => {
      const upcoming = UPCOMING.find((e) => e.code === code);
      if (upcoming) return { ...upcoming, isUpcoming: true };
      const archived = ARCHIVE.find((e) => e.code === code);
      return archived ? { ...archived, isUpcoming: false } : null;
    })
    .filter((x): x is PlayedEvent => x !== null);

  const prevNumber = ((idx - 1 + RESIDENTS.length) % RESIDENTS.length) + 1;
  const nextNumber = ((idx + 1) % RESIDENTS.length) + 1;

  return (
    <section data-screen-label={`artist ${a.slug}`}>
      <ArtistHero artist={a} index={idx} setsCount={played.length} />

      <SignatureList artist={a} />

      <PlayedList played={played} />

      <ArtistLinks artist={a} />

      <BookingBlock
        artist={a}
        showCta
        onRequestBooking={() => setBookOpen(true)}
      />

      <ArtNav prev={prev} next={next} prevNumber={prevNumber} nextNumber={nextNumber} />

      {bookOpen && (
        <BookingForm artist={a} mode="modal" onClose={() => setBookOpen(false)} />
      )}
    </section>
  );
}
